import React, { useImperativeHandle, useRef } from 'react';
import moment from 'moment';
import QRCode from 'qrcode.react';
import intl from 'react-intl-universal';
import { DATE_TIME_FORMAT } from '@/config';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset, TenantSignStorage } from '@/utils/intl';
import paymentBillSrc from '@/assets/img/payment_bill.png';
import paymentBillUnionPaySrc from '@/assets/img/payment_bill_unionPay.png';
// import wyImgSrc from '../imgs/wywkLog.png';
import weChatImgSrc from '../imgs/weChat.png';
import alipayImgSrc from '../imgs/alipay.png';
import unionPayImgSrc from '../imgs/unionPay.png';
import { IPaymentOrderDetail } from '../helper';
import styles from './index.module.scss';

interface PaymentOrderContentRef {
  getChildrenRef: () => React.ReactElement;
}

interface PaymentOrderContentProps {
  detail?: IPaymentOrderDetail;
}
const PaymentOrderContent = React.forwardRef<PaymentOrderContentRef, PaymentOrderContentProps>(
  ({ detail }, ref) => {
    const containerRef = useRef<any>();
    const scanCodeRef = useRef<any>();
    // 商户名称
    const merchantName = TenantSignStorage.getLocalConfig()?.marchantName || '--';
    const slogan = TenantSignStorage.getLocalConfig()?.slogan || '--';
    const serviceTel = TenantSignStorage.getLocalConfig()?.serviceTel || '--';
    const joinTel = TenantSignStorage.getLocalConfig()?.joinTel || '--';
    const logo = TenantSignStorage.getLocalConfig()?.icon2x;

    useImperativeHandle(
      ref,
      () => ({
        getChildrenRef: () => containerRef.current,
        getScanCodeRef: () => scanCodeRef.current,
      }),
      [],
    );

    return (
      <>
        {/* 屏幕外显示所有，包含二维码 */}
        <div
          className={styles.billContent}
          ref={containerRef}
          style={{ position: 'fixed', left: '-50000px', bottom: '-50000px', zIndex: -10000 }}
        >
          <div className={styles.title}>
            <img
              src={logo}
              alt={intl.get('bill_pay_order_list_modal_wy_logo')}
              crossOrigin="anonymous"
            />
            <div>
              {merchantName}（{detail?.shopName}）
            </div>
          </div>
          <div className={styles.subject}>
            {intl.get('bill_pay_order_list_modal_column_pay_order_no')}
          </div>
          {/* 订单详情 */}
          <div className={styles.orderDetail}>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {intl.get('bill_pay_order_list_payment_order_order_num')}
              </div>
              <div className={styles.detailContent}>{detail?.orderNo}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {intl.get('bill_pay_order_list_payment_order_time')}
              </div>
              <div className={styles.detailContent}>
                {detail?.createTime ? getTimeByUtcOffset(detail?.createTime, DATE_TIME_FORMAT) : ''}
              </div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>{intl.get('app_constants_guest_name')}</div>
              <div className={styles.detailContent}>{detail?.customerNameMock || '-'}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {intl.get('bill_pay_order_list_modal_store_address')}
              </div>
              <div className={styles.detailContent}>{detail?.shopAddress}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {intl.get('bill_pay_order_list_modal_store_phone')}
              </div>
              <div className={styles.detailContent}>{detail?.shopTelephone}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {intl.get('bill_pay_order_list_modal_make_order_person')}
              </div>
              <div className={styles.detailContent}>{detail?.employeeName}</div>
            </div>
          </div>

          {/* 支付详情 */}
          {detail?.items?.length ? (
            <div className={styles.payDetail}>
              <div className={styles.payDetailItem}>
                <div className={styles.payLabel}>
                  {intl.get('bill_pay_order_list_modal_pay_detail')}
                </div>
                <div className={styles.payContent}>&nbsp;</div>
              </div>
              {detail?.items?.map((item) => (
                <div className={styles.payDetailItem} key={item.name}>
                  <div className={styles.payLabel}>{item.name}</div>
                  <div className={styles.payContent}>{parseFen2Currency(item.amt)}</div>
                </div>
              ))}
            </div>
          ) : null}

          {/* 二维码 */}
          <div className={styles.codeScan}>
            <div className={styles.sum}>
              <div>{intl.get('bill_pay_order_list_modal_total')}</div>
              <div className={styles.total}>{parseFen2Currency(detail?.totalAmt)}</div>
            </div>
            <div className={styles.pointing}>
              {intl.get('bill_pay_order_list_modal_scan_qrcode')}
            </div>
            <div className={styles.code} ref={scanCodeRef}>
              <QRCode value={detail?.qrcodeUrl} size={212} />
            </div>
            <div className={styles.bottomLogo}>
              <img
                src={alipayImgSrc}
                alt={intl.get('app_constants_alipay')}
                crossOrigin="anonymous"
              />
              <img
                src={weChatImgSrc}
                alt={intl.get('app_constants_wechat')}
                crossOrigin="anonymous"
              />
              {/* 76*38 */}
              {detail?.payChannel === '10' ? (
                <img
                  src={unionPayImgSrc}
                  alt={intl.get('app_constants_wechat')}
                  crossOrigin="anonymous"
                />
              ) : null}
            </div>
            <div className={styles.prompt}>
              {intl.get('bill_pay_order_list_modal_question_contact_front')}
            </div>
          </div>

          {/* 加盟热线 */}
          <div>
            <div className={styles.slogan}>{slogan}</div>
            <div className={styles.hotline}>
              <div className={styles.serviceHotline}>
                {intl.get('bill_pay_order_list_modal_wy_service_phone')}
                <span>{serviceTel}</span>
              </div>
              <div className={styles.leagueHotline}>
                {intl.get('bill_pay_order_list_modal_wy_join_phone')}
                <span>{joinTel}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 静态图片，预览 */}
        {detail?.payChannel === '10' ? (
          <img
            className={styles.previewImg}
            src={paymentBillUnionPaySrc}
            alt={intl.get('bill_pay_order_list_modal_review_img')}
          />
        ) : (
          <img
            className={styles.previewImg}
            src={paymentBillSrc}
            alt={intl.get('bill_pay_order_list_modal_review_img')}
          />
        )}
      </>
    );
  },
);

export default PaymentOrderContent;
